import {
  PropsWithChildren,
  ReactElement,
  ReactNode,
  Children,
  Fragment,
  cloneElement,
  isValidElement,
  useMemo
} from "react";
import MenuFilters from "@/components/ExpandedFilters/MenuFilters";
import ExpandedFiltersWrapper from "@/components/ExpandedFilters/ExpandedFiltersWrapper";
import IconButton from "@/components/IconButton";
import Button from "@/components/Button";
import Chip from "@/components/Chip";
import Checkbox from "@/components/Checkbox";
import Menu, { MenuItem } from "@/components/Menu";
import {
  ActionCluster,
  consolidateActions,
  DEFAULT_MAX_INLINE_ACTIONS,
  MENU_ONLY_CLUSTER_LABEL,
  type Action
} from "@/components/MenuButton";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import pluralize from "@/lib/pluralize";
import { TableAction } from "./TableHeading";
import type { BulkSelectionProps } from "@/hooks/useBulkSelection";
import classNames from "classnames";
import { TableNoDataEmptyProvider, useTableNoDataEmpty } from "./TableNoDataEmptyContext";

export { TableNoDataEmptyProvider };

/**
 * Configuration for a filter chip that can be displayed in TableControls
 * @property label - The text label displayed on the chip
 * @property onClick - Handler called when the chip is clicked
 * @property selected - Whether the chip is currently selected/active (checkmark automatically shown when selected)
 * @property softDisabled - Keeps a selected chip focusable but not togglable (last remaining required filter)
 */
export interface FilterChipConfig {
  label: string;
  onClick?: () => void;
  selected?: boolean;
  softDisabled?: boolean;
}

/**
 * Props for TableControls component
 * @property pagination - Optional pagination element (automatically hidden when isSelectedOnly is true, or when eligible non-`menuOnly` page `actions` occupy the pager slot)
 * @property actions - Page actions rendered with ActionCluster. Eligible (non-`menuOnly`) actions occupy the desktop pager slot and hide header pagination. When every action is `menuOnly`, header pagination stays next to the cluster. Compact layouts (`< lg`) put them on the same row as the sort control. Desktop and tablet keep `maxInline` 2. Phones (`< md`) collapse a pair into an Actions menu. Hidden during bulk selection.
 * @property activeFilters - Number of active filters for badge display
 * @property onSortClick - Optional sort button handler for mobile
 * @property useExpandedFilters - Enable ExpandedFiltersWrapper for automatic filter overflow on desktop (like People table)
 * @property expandedFiltersState - Filter state for ExpandedFiltersWrapper (overflow wrapping needs this plus onClearAllFilters)
 * @property onClearAllFilters - Clear all filters handler (overflow wrapping needs this plus expandedFiltersState)
 * @property bulkActions - Bulk action buttons shown when rows are selected (replaces filters)
 * @property rowSelectionCount - Number of selected rows (triggers bulk action display, can use bulkSelection instead)
 * @property onShowSelectedOnly - Handler for "Show Selected Only" chip toggle (can use bulkSelection instead)
 * @property isSelectedOnly - Whether "Show Selected Only" is currently active (can use bulkSelection instead)
 * @property bulkSelection - Grouped bulk selection props from useBulkSelection hook (optional convenience)
 * @property onDeselectAll - Handler for "Deselect All" button
 * @property totalRowCount - Number of currently selected rows for "Deselect All X Rows" label
 * @property onToggleAllRows - Handler to select/deselect all rows for auto-generated mobile checkbox. Called with true to select all, false to deselect all.
 * @property allRowsSelected - Whether all visible rows are currently selected (for checkbox checked state)
 * @property someRowsSelected - Whether some (but not all) rows are selected (for checkbox indeterminate state)
 * @property filterChips - Array of filter chip configurations to display below search or page `actions`. With neither, chips are the only header control (no extra top margin). Chips handle their own filter states and are shown by default (not conditional on row selection). Use chips for quick-access toggling of table state that should always be visible (unlike filters which can become hidden in side sheets on smaller devices).
 * @property filterChipsAriaLabel - Accessible name for the filter chip group
 * @property mutuallyExclusiveChips - When true, only one chip can be selected at a time. Clicking a chip will automatically deselect all other chips.
 * @property onSelectAllAcrossPages - Handler to select all matching rows across all pages. When provided, a "Select all X matching" text button appears in the selection bar after rows are selected.
 * @property selectAllAcrossPagesLoading - Whether the cross-page selection is loading (shows "Selecting..." label on the button)
 * @property totalMatchingCount - Total number of matching rows across all pages (used in the "Select all X matching" button label)
 * @property afterSearch - Control rendered after the first child (usually SearchInput). Stays out of the compact filter sheet: centered below search on compact layouts, to the right of search on desktop. Use for always-visible companions such as a month pager.
 */
export interface TableControlsProps {
  pagination?: ReactNode;
  actions?: Action[];
  activeFilters?: number;
  onSortClick?: () => void;
  useExpandedFilters?: boolean;
  expandedFiltersState?: Record<string, boolean>;
  onClearAllFilters?: () => void;
  bulkActions?: TableAction[];
  rowSelectionCount?: number;
  onShowSelectedOnly?: () => void;
  isSelectedOnly?: boolean;
  bulkSelection?: BulkSelectionProps;
  onDeselectAll?: () => void;
  totalRowCount?: number;
  onToggleAllRows?: (checked: boolean) => void;
  allRowsSelected?: boolean;
  someRowsSelected?: boolean;
  filterChips?: FilterChipConfig[];
  filterChipsAriaLabel?: string;
  mutuallyExclusiveChips?: boolean;
  onSelectAllAcrossPages?: () => void;
  selectAllAcrossPagesLoading?: boolean;
  totalMatchingCount?: number;
  afterSearch?: ReactNode;
}

function disableSearchControl(child: ReactNode): ReactNode {
  if (!isValidElement(child)) return child;
  return cloneElement(child as ReactElement<{ disabled?: boolean }>, { disabled: true });
}

function SearchWithAfterSearch({ search, afterSearch }: { search: ReactNode; afterSearch?: ReactNode }) {
  if (search == null && afterSearch == null) {
    return null;
  }

  if (!afterSearch) {
    return <div className="w-full lg:max-w-[320px]">{search}</div>;
  }

  return (
    <div className="flex flex-col lg:flex-row lg:items-center gap-3 min-w-0 w-full">
      <div className="w-full min-w-0 lg:w-80">{search}</div>
      <div className="flex-shrink-0 self-center lg:self-auto" data-testid="table-controls-after-search">
        {afterSearch}
      </div>
    </div>
  );
}

function pageActionsOccupyPagerSlot(actions: Action[]): boolean {
  return actions.some((action) => !action.menuOnly);
}

function TableControlsActionsCluster({ actions }: { actions: Action[] }) {
  const { isMobile } = useIsCompactLayout();
  if (actions.length === 0) return null;

  const maxInline = isMobile ? 1 : DEFAULT_MAX_INLINE_ACTIONS;
  const consolidation = consolidateActions({ actions, maxInline });
  const menuButtonLabel =
    isMobile && consolidation.inlineActions.length === 0 && consolidation.menuItems.length > 0
      ? MENU_ONLY_CLUSTER_LABEL
      : undefined;

  return (
    <div className="flex items-center gap-3" data-testid="table-controls-actions">
      <ActionCluster actions={actions} maxInline={maxInline} menuButtonLabel={menuButtonLabel} />
    </div>
  );
}

function DesktopEndSlot({
  occupyPagerSlot,
  showPageActions,
  compactLayout,
  actions,
  pagination,
  selectedOnly
}: {
  occupyPagerSlot: boolean;
  showPageActions: boolean;
  compactLayout: boolean;
  actions: Action[];
  pagination?: ReactNode;
  selectedOnly: boolean;
}) {
  if (compactLayout && showPageActions) {
    return null;
  }

  if (occupyPagerSlot) {
    return (
      <div className="flex items-center self-start lg:self-auto">
        <TableControlsActionsCluster actions={actions} />
      </div>
    );
  }

  if (showPageActions) {
    return (
      <div className="flex items-center gap-3 self-start lg:self-auto">
        {!selectedOnly && pagination}
        <TableControlsActionsCluster actions={actions} />
      </div>
    );
  }

  if (!selectedOnly && pagination) {
    return <div className="hidden lg:flex items-center">{pagination}</div>;
  }

  return null;
}

function CompactEndRow({
  occupyPagerSlot,
  showPageActions,
  pageActions,
  pagination,
  selectedOnly,
  mobileCheckbox,
  onSortClick
}: {
  occupyPagerSlot: boolean;
  showPageActions: boolean;
  pageActions?: ReactNode;
  pagination?: ReactNode;
  selectedOnly: boolean;
  mobileCheckbox?: ReactNode;
  onSortClick?: () => void;
}) {
  const pager = !selectedOnly && pagination ? pagination : null;
  let compactRight: ReactNode = null;
  if (occupyPagerSlot) {
    compactRight = pageActions ?? null;
  } else if (showPageActions) {
    compactRight =
      pager || pageActions ? (
        <div className="flex items-center gap-3">
          {pager}
          {pageActions}
        </div>
      ) : null;
  } else {
    compactRight = pager;
  }

  if (!compactRight && !mobileCheckbox && !onSortClick) {
    return null;
  }

  const compactJustify =
    mobileCheckbox || onSortClick ? "justify-between" : occupyPagerSlot ? "justify-start" : "justify-center";

  return (
    <div
      className={`flex items-center lg:hidden ${compactRight ? "mt-3" : "mt-4"} ${compactJustify}`}
      data-testid="table-controls-compact-end"
    >
      {(mobileCheckbox || onSortClick) && (
        <div className="flex items-center gap-2">
          {mobileCheckbox}
          {onSortClick && (
            <IconButton
              name="sort_by_alpha"
              onClick={onSortClick}
              title="Sort Table"
              data-testid="mobile-sort-button"
              skipMargin
              className="-ml-2"
            />
          )}
        </div>
      )}
      {compactRight}
    </div>
  );
}

/**
 * TableControls component provides consistent responsive layout for table filters and search:
 * - Search/filter inputs (via children)
 * - Optional pagination (shown inline on desktop, on separate line below on mobile)
 * - Automatic filter sheet pattern on mobile when multiple filters exist
 * - Automatic sizing: First child (SearchInput) max-w-[320px], filters min-w-[160px] max-w-[260px]
 * - Components with inline minWidth (like DateSelect: 260px for ranges) automatically respected
 * - Filters with data-filter-size="custom" skip the 160-260px clamp on the default desktop inline path
 * - For many filters: Set useExpandedFilters={true} for automatic overflow (uses default 160-260px sizing)
 *
 * Note: Page actions belong in TableControls.actions (ActionCluster) — index creates and
 * entity-detail actions-only chrome. TableHeading.actions is for headings that still have
 * title or body copy. Do not put the same actions on both.
 *
 * **Row Selection Tables (Automatic Mobile Checkbox):**
 * For tables with bulk actions, provide `onToggleAllRows`, `allRowsSelected`, and `someRowsSelected` props.
 * TableControls will automatically generate a mobile select-all checkbox.
 * The component will warn you in development mode if these props are missing.
 *
 * **When to Show TableControls:**
 * - Always pass TableControls via Table `renderControls`
 * - Table hides the slot on true no-data (`!hasActiveFilters`) unless TableControls has `actions` or a SearchInput child (including PeopleSearchInput wrappers and a react-hook-form Controller search slot). Filter-only children do not keep the toolbar.
 * - When that no-data toolbar is shown, SearchInput is disabled (including wrappers such as PeopleSearchInput) so typing cannot surface filters; ActionCluster stays enabled
 * - Do not gate `renderControls` on `data.length`
 *
 * **Automatic Mobile Behavior:**
 * - 1 child (just search): Shown inline, full width
 * - 2+ children (search + filters): First child inline, rest in side sheet via filter icon
 * - afterSearch: Centered below search on compact layouts, to the right of search on desktop; never placed in the compact filter sheet
 * - Page `actions`: sit on the same compact row as the sort control. A pair stays inline from tablet up; phones (`< md`) collapse both into an Actions menu. Eligible (non-`menuOnly`) actions occupy the pager slot and stay left-aligned without sort or selection. Menu-only actions keep the compact pager in the same end group.
 *
 * **Desktop Behavior:**
 * - Standard: All children shown inline with automatic sizing (160-260px)
 * - With useExpandedFilters: Filters use standard sizing (160-260px), dynamically overflow into "Filters" button
 * - Pagination displays inline with filters. Eligible page `actions` replace that pager; `menuOnly`-only actions sit beside it.
 *
 * @example
 * Standard mode: SearchInput (320px max) + Select filters (160-260px default)
 * DateSelect: Has built-in minWidth (260px for ranges, 160px single) - no wrapper needed
 * Many filters: Set useExpandedFilters={true} for automatic overflow management
 */
export function TableControls({
  pagination,
  actions = [],
  children,
  activeFilters,
  onSortClick,
  useExpandedFilters = true,
  expandedFiltersState,
  onClearAllFilters,
  bulkActions = [],
  rowSelectionCount = 0,
  onShowSelectedOnly,
  isSelectedOnly = false,
  bulkSelection,
  onDeselectAll,
  totalRowCount,
  onToggleAllRows,
  allRowsSelected = false,
  someRowsSelected = false,
  filterChips = [],
  filterChipsAriaLabel,
  mutuallyExclusiveChips = false,
  onSelectAllAcrossPages,
  selectAllAcrossPagesLoading = false,
  totalMatchingCount,
  afterSearch
}: PropsWithChildren<TableControlsProps>) {
  const { isCompactLayout } = useIsCompactLayout();
  const count = bulkSelection?.rowSelectionCount ?? rowSelectionCount;
  const onShowSelected = bulkSelection?.onShowSelectedOnly ?? onShowSelectedOnly;
  const selectedOnly = bulkSelection?.isSelectedOnly ?? isSelectedOnly;

  const processedFilterChips = useMemo(() => {
    if (!mutuallyExclusiveChips || filterChips.length === 0) {
      return filterChips;
    }

    return filterChips.map((chipConfig, index) => {
      const originalOnClick = chipConfig.onClick;
      if (!originalOnClick) {
        return chipConfig;
      }

      return {
        ...chipConfig,
        onClick: () => {
          if (chipConfig.selected) {
            originalOnClick();
            return;
          }

          originalOnClick();

          filterChips.forEach((otherChip, otherIndex) => {
            if (otherIndex !== index && otherChip.selected && otherChip.onClick) {
              otherChip.onClick();
            }
          });
        }
      };
    });
  }, [filterChips, mutuallyExclusiveChips]);

  const isNoDataEmpty = useTableNoDataEmpty();
  const allChildren = Children.toArray(children);
  const childArray = (isNoDataEmpty ? allChildren.slice(0, 1) : allChildren).map((child, index) =>
    isNoDataEmpty && index === 0 ? disableSearchControl(child) : child
  );

  const hasMultipleChildren = childArray.length > 1;
  const hasRowSelection = count > 0;
  const showPageActions = !hasRowSelection && actions.length > 0;
  const occupyPagerSlot = showPageActions && pageActionsOccupyPagerSlot(actions);

  const filterChipsRow =
    processedFilterChips.length > 0 ? (
      <div
        className={classNames("flex items-center gap-4 flex-wrap", {
          "mt-3": childArray.length > 0 || afterSearch != null || showPageActions
        })}
        role={filterChipsAriaLabel ? "group" : undefined}
        aria-label={filterChipsAriaLabel}
      >
        {processedFilterChips.map((chipConfig, index) => (
          <Chip
            key={chipConfig.label ?? index}
            label={chipConfig.label}
            onClick={chipConfig.onClick}
            selected={chipConfig.selected}
            softDisabled={chipConfig.softDisabled}
          />
        ))}
      </div>
    ) : null;
  const compactPageActions =
    showPageActions && isCompactLayout ? <TableControlsActionsCluster actions={actions} /> : undefined;
  const visibleAfterSearch = isNoDataEmpty ? undefined : afterSearch;
  const visibleOnSortClick = isNoDataEmpty ? undefined : onSortClick;
  const visibleFilterChipsRow = isNoDataEmpty ? null : filterChipsRow;

  const mobileCheckbox =
    bulkActions.length > 0 && onToggleAllRows ? (
      <Checkbox
        name="select-all"
        checked={allRowsSelected}
        indeterminate={someRowsSelected}
        onChange={(checked) => {
          onToggleAllRows(checked);
        }}
      />
    ) : undefined;

  if (
    childArray.length === 0 &&
    afterSearch == null &&
    !pagination &&
    !onSortClick &&
    !hasRowSelection &&
    processedFilterChips.length > 0 &&
    !mobileCheckbox &&
    !showPageActions
  ) {
    return <div>{filterChipsRow}</div>;
  }

  if (hasMultipleChildren) {
    const [firstChild, ...restChildren] = childArray;

    const filterItems = restChildren
      .filter((child: any) => child != null)
      .map((child: any) => {
        const childName = child?.props?.name;
        const hasCustomSizeAttribute = child?.props?.["data-filter-size"] === "custom";
        const hasInlineMinWidth = !!child?.props?.style?.minWidth;

        const hasCustomSizing = hasCustomSizeAttribute || hasInlineMinWidth;

        return {
          component: child,
          wrappedComponent: hasCustomSizing ? (
            child
          ) : (
            <div className="min-w-[160px] max-w-[200px]" data-name={childName}>
              {child}
            </div>
          ),
          name: childName,
          hasCustomSizing
        };
      });

    return (
      <div>
        <div
          className={classNames("flex flex-col lg:flex-row lg:items-center", occupyPagerSlot ? "gap-6" : "gap-3")}
          data-testid="table-controls-toolbar"
        >
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 flex-1">
            {hasRowSelection ? (
              <>
                <div className="hidden lg:flex lg:items-center gap-3 flex-1">
                  <SearchWithAfterSearch search={firstChild} afterSearch={visibleAfterSearch} />
                  {bulkActions.map((action, index) =>
                    action.render ? (
                      <Fragment key={index}>{action.render()}</Fragment>
                    ) : (
                      <Button
                        key={index}
                        variant={action.variant || "outlined"}
                        icon={action.icon}
                        onClick={action.onClick}
                        disabled={action.disabled}
                        testId={action.testId}
                      >
                        {action.label}
                      </Button>
                    )
                  )}
                </div>

                <div className="flex lg:hidden flex-col gap-3 w-full">
                  <SearchWithAfterSearch search={firstChild} afterSearch={visibleAfterSearch} />
                  {bulkActions.length > 0 && (
                    <div className="flex gap-2 items-center flex-wrap">
                      {bulkActions[0].render ? (
                        bulkActions[0].render()
                      ) : (
                        <Button
                          variant={bulkActions[0].variant || "outlined"}
                          icon={bulkActions[0].icon}
                          onClick={bulkActions[0].onClick}
                          disabled={bulkActions[0].disabled}
                          testId={bulkActions[0].testId}
                        >
                          {bulkActions[0].label}
                        </Button>
                      )}

                      {bulkActions.length === 2 &&
                        (bulkActions[1].render ? (
                          bulkActions[1].render()
                        ) : (
                          <Button
                            variant={bulkActions[1].variant || "outlined"}
                            icon={bulkActions[1].icon}
                            onClick={bulkActions[1].onClick}
                            disabled={bulkActions[1].disabled}
                            testId={bulkActions[1].testId}
                          >
                            {bulkActions[1].label}
                          </Button>
                        ))}

                      {bulkActions.length > 2 && (
                        <Menu
                          AnchorComponent={<IconButton name="pending" data-testid="bulk-actions-menu-button" />}
                          menuItems={bulkActions.slice(1).map(
                            (action) =>
                              ({
                                label: action.label,
                                onClick: action.onClick,
                                ...(action.icon ? { iconName: action.icon } : {}),
                                dataTestId: action.testId
                              }) as MenuItem
                          )}
                        />
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {useExpandedFilters && expandedFiltersState && onClearAllFilters ? (
                  <div className="hidden lg:flex lg:items-center gap-3 flex-1">
                    <SearchWithAfterSearch search={firstChild} afterSearch={visibleAfterSearch} />
                    <ExpandedFiltersWrapper
                      items={filterItems}
                      filters={expandedFiltersState}
                      onClearAll={onClearAllFilters}
                      activeFilters={activeFilters}
                    />
                  </div>
                ) : (
                  <div className="hidden lg:flex lg:items-center gap-3 flex-1">
                    <SearchWithAfterSearch search={firstChild} afterSearch={visibleAfterSearch} />
                    {filterItems.map((item, index) => (
                      <div
                        key={index}
                        className={item.hasCustomSizing ? "flex-shrink-0" : "min-w-[160px] max-w-[260px] flex-shrink-0"}
                      >
                        {item.component}
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex lg:hidden items-center gap-3 flex-1 min-w-0">
                  <SearchWithAfterSearch search={firstChild} afterSearch={visibleAfterSearch} />
                  {restChildren.length > 0 && (
                    <MenuFilters
                      content={
                        <div className="flex flex-col gap-6 py-6">
                          {restChildren.map((child, index) => (
                            <div key={index}>{child}</div>
                          ))}
                          {useExpandedFilters && onClearAllFilters && !!activeFilters && activeFilters > 0 && (
                            <Button variant="text" onClick={onClearAllFilters}>
                              Reset All
                            </Button>
                          )}
                        </div>
                      }
                      activeFilters={activeFilters}
                    />
                  )}
                </div>
              </>
            )}
          </div>

          <DesktopEndSlot
            occupyPagerSlot={occupyPagerSlot}
            showPageActions={showPageActions}
            compactLayout={isCompactLayout}
            actions={actions}
            pagination={pagination}
            selectedOnly={selectedOnly}
          />
        </div>

        {!hasRowSelection && visibleFilterChipsRow}

        {hasRowSelection && (onShowSelected || onDeselectAll || onSelectAllAcrossPages) && (
          <div className={classNames("flex items-center gap-4 mt-3", { "flex-wrap": onSelectAllAcrossPages })}>
            {onShowSelected && (
              <div className="flex items-center gap-2 md:mb-0">
                {onSelectAllAcrossPages && (
                  <span className="text-label-large">
                    {count} {pluralize(count, "row")} selected
                  </span>
                )}
                {onShowSelected && <Chip onClick={onShowSelected} label="Show Selected Only" selected={selectedOnly} />}
              </div>
            )}
            {onSelectAllAcrossPages && totalMatchingCount !== count && (
              <Button
                variant="text"
                onClick={onSelectAllAcrossPages}
                disabled={selectAllAcrossPagesLoading}
                testId="select-all-across-pages"
              >
                {selectAllAcrossPagesLoading ? "Selecting..." : `Select All ${totalMatchingCount} Rows`}
              </Button>
            )}
            {onDeselectAll && totalRowCount && (
              <Button variant="text" onClick={onDeselectAll}>
                Deselect All {totalRowCount} {pluralize(totalRowCount, "Row")}
              </Button>
            )}
          </div>
        )}

        <CompactEndRow
          occupyPagerSlot={occupyPagerSlot}
          showPageActions={showPageActions}
          pageActions={compactPageActions}
          pagination={pagination}
          selectedOnly={selectedOnly}
          mobileCheckbox={mobileCheckbox}
          onSortClick={visibleOnSortClick}
        />
      </div>
    );
  }

  return (
    <div>
      <div
        className={classNames(
          "flex flex-col lg:flex-row lg:items-center lg:justify-between",
          occupyPagerSlot ? "gap-6" : "gap-3"
        )}
        data-testid="table-controls-toolbar"
      >
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 flex-1">
          {hasRowSelection && bulkActions.length > 0 ? (
            <>
              <div className="hidden lg:flex lg:items-center gap-3 flex-1">
                <SearchWithAfterSearch search={childArray[0]} afterSearch={visibleAfterSearch} />
                {childArray.slice(1).map((child, index) => (
                  <div key={index} className="w-full lg:max-w-[320px]">
                    {child}
                  </div>
                ))}
                {bulkActions.map((action, index) =>
                  action.render ? (
                    <Fragment key={`action-${index}`}>{action.render()}</Fragment>
                  ) : (
                    <Button
                      key={`action-${index}`}
                      variant={action.variant || "outlined"}
                      icon={action.icon}
                      onClick={action.onClick}
                      disabled={action.disabled}
                      testId={action.testId}
                    >
                      {action.label}
                    </Button>
                  )
                )}
              </div>

              <div className="flex lg:hidden flex-col gap-3 w-full">
                <SearchWithAfterSearch search={childArray[0]} afterSearch={visibleAfterSearch} />
                {childArray.slice(1).map((child, index) => (
                  <div key={index} className="w-full">
                    {child}
                  </div>
                ))}
                <div className="flex gap-2 items-center flex-wrap">
                  {bulkActions[0].render ? (
                    bulkActions[0].render()
                  ) : (
                    <Button
                      variant={bulkActions[0].variant || "outlined"}
                      icon={bulkActions[0].icon}
                      onClick={bulkActions[0].onClick}
                      disabled={bulkActions[0].disabled}
                      testId={bulkActions[0].testId}
                    >
                      {bulkActions[0].label}
                    </Button>
                  )}

                  {bulkActions.length === 2 &&
                    (bulkActions[1].render ? (
                      bulkActions[1].render()
                    ) : (
                      <Button
                        variant={bulkActions[1].variant || "outlined"}
                        icon={bulkActions[1].icon}
                        onClick={bulkActions[1].onClick}
                        disabled={bulkActions[1].disabled}
                        testId={bulkActions[1].testId}
                      >
                        {bulkActions[1].label}
                      </Button>
                    ))}

                  {bulkActions.length > 2 && (
                    <Menu
                      AnchorComponent={<IconButton name="pending" data-testid="bulk-actions-menu-button" />}
                      menuItems={bulkActions.slice(1).map(
                        (action) =>
                          ({
                            label: action.label,
                            onClick: action.onClick,
                            ...(action.icon ? { iconName: action.icon } : {}),
                            dataTestId: action.testId
                          }) as MenuItem
                      )}
                    />
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <SearchWithAfterSearch search={childArray[0]} afterSearch={visibleAfterSearch} />
              {childArray.slice(1).map((child, index) => (
                <div key={index} className="min-w-[140px] max-w-[200px]">
                  {child}
                </div>
              ))}
            </>
          )}
        </div>

        <DesktopEndSlot
          occupyPagerSlot={occupyPagerSlot}
          showPageActions={showPageActions}
          compactLayout={isCompactLayout}
          actions={actions}
          pagination={pagination}
          selectedOnly={selectedOnly}
        />
      </div>

      {!hasRowSelection && visibleFilterChipsRow}

      {hasRowSelection && (onShowSelected || onDeselectAll || onSelectAllAcrossPages) && (
        <div className={classNames("flex items-center gap-4 mt-3", { "flex-wrap": onSelectAllAcrossPages })}>
          {onShowSelected && (
            <div className="flex items-center gap-2 md:mb-0">
              {onSelectAllAcrossPages && (
                <span className="text-label-large">
                  {count} {pluralize(count, "row")} selected
                </span>
              )}
              {onShowSelected && <Chip onClick={onShowSelected} label="Show Selected Only" selected={selectedOnly} />}
            </div>
          )}
          {onSelectAllAcrossPages && totalMatchingCount !== count && (
            <Button
              variant="text"
              onClick={onSelectAllAcrossPages}
              disabled={selectAllAcrossPagesLoading}
              testId="select-all-across-pages"
            >
              {selectAllAcrossPagesLoading ? "Selecting..." : `Select All ${totalMatchingCount} Rows`}
            </Button>
          )}
          {onDeselectAll && totalRowCount && (
            <Button variant="text" onClick={onDeselectAll}>
              Deselect All {totalRowCount} {pluralize(totalRowCount, "Row")}
            </Button>
          )}
        </div>
      )}

      <CompactEndRow
        occupyPagerSlot={occupyPagerSlot}
        showPageActions={showPageActions}
        pageActions={compactPageActions}
        pagination={pagination}
        selectedOnly={selectedOnly}
        mobileCheckbox={mobileCheckbox}
        onSortClick={visibleOnSortClick}
      />
    </div>
  );
}
